import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../../admin/api';
import {
  AdminActionBar,
  AdminDetailPanel,
  AdminNoDataState,
  AdminStatusChip,
  AdminTimeline,
} from '../shared';
import SosSignalActionDialog from './SosSignalActionDialog';
import {
  buildTimelineItems,
  formatDateTime,
  formatServerDateTime,
  formatValue,
  getSignalFlags,
  parseServerDateTime,
} from './sosShared';

const DRAWER_WIDTH = 720;

function DetailLoadingState() {
  return (
    <Stack spacing={3}>
      <Skeleton variant="rounded" height={116} />
      <Skeleton variant="rounded" height={140} />
      <Skeleton variant="rounded" height={220} />
      <Skeleton variant="rounded" height={220} />
    </Stack>
  );
}

export default function SosSignalDetailsDrawer({
  open,
  signalId,
  initialAction,
  onInitialActionHandled,
  onClose,
  onSignalUpdated,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [detailState, setDetailState] = useState({
    loading: false,
    error: null,
    data: null,
  });
  const [dialogState, setDialogState] = useState({
    mode: null,
    open: false,
    submitting: false,
    error: null,
    draft: {
      note: '',
      expires_at: null,
    },
  });

  const loadDetail = useCallback(async () => {
    if (!signalId) {
      return;
    }

    setDetailState({
      loading: true,
      error: null,
      data: null,
    });

    try {
      const response = await fetchAdmin(`/sos/${signalId}`);
      setDetailState({
        loading: false,
        error: null,
        data: response,
      });
    } catch (error) {
      setDetailState({
        loading: false,
        error: toErrorMessage(error),
        data: null,
      });
    }
  }, [signalId]);

  useEffect(() => {
    if (!open || !signalId) {
      return undefined;
    }

    loadDetail();
    return undefined;
  }, [loadDetail, open, signalId]);

  const signal = detailState.data?.signal || null;
  const flags = getSignalFlags(signal);
  const signalTimelineItems = useMemo(
    () => buildTimelineItems(detailState.data?.events || [], 'signal'),
    [detailState.data]
  );
  const caseTimelineItems = useMemo(
    () => buildTimelineItems(detailState.data?.case_events || [], 'case'),
    [detailState.data]
  );

  const openActionDialog = useCallback((mode) => {
    setDialogState({
      mode,
      open: true,
      submitting: false,
      error: null,
      draft: {
        note: '',
        expires_at: parseServerDateTime(signal?.expires_at),
      },
    });
  }, [signal?.expires_at]);

  useEffect(() => {
    if (!open || !initialAction || !signal) {
      return;
    }

    const actionAllowed =
      (initialAction === 'resolve' && flags.canResolve) ||
      (initialAction === 'extend' && flags.canExtend) ||
      (initialAction === 'remove_block' && flags.canRemoveBlock);

    if (!actionAllowed || dialogState.open) {
      return;
    }

    openActionDialog(initialAction);
    onInitialActionHandled();
  }, [
    dialogState.open,
    flags.canExtend,
    flags.canRemoveBlock,
    flags.canResolve,
    initialAction,
    onInitialActionHandled,
    open,
    openActionDialog,
    signal,
  ]);

  const closeActionDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      open: false,
      submitting: false,
      error: null,
    }));
  };

  const handleDraftChange = (field, value) => {
    setDialogState((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        [field]: value,
      },
    }));
  };

  const handleSubmitAction = async () => {
    if (!signalId || !dialogState.mode) {
      return;
    }

    if (!dialogState.draft.note.trim()) {
      setDialogState((prev) => ({
        ...prev,
        error: 'A note is required for this SOS action.',
      }));
      return;
    }

    if (dialogState.mode === 'extend' && !formatServerDateTime(dialogState.draft.expires_at)) {
      setDialogState((prev) => ({
        ...prev,
        error: 'Select a valid monitoring expiry before submitting.',
      }));
      return;
    }

    const payload =
      dialogState.mode === 'extend'
        ? {
            expires_at: formatServerDateTime(dialogState.draft.expires_at),
            note: dialogState.draft.note.trim(),
          }
        : {
            note: dialogState.draft.note.trim(),
          };

    setDialogState((prev) => ({
      ...prev,
      submitting: true,
      error: null,
    }));

    try {
      await postAdmin(`/sos/${signalId}/${dialogState.mode}`, payload);
      enqueueSnackbar('SOS action completed successfully.', { variant: 'success' });
      closeActionDialog();
      await loadDetail();
      onSignalUpdated();
    } catch (error) {
      setDialogState((prev) => ({
        ...prev,
        submitting: false,
        error: toErrorMessage(error),
      }));
    }
  };

  const summaryItems = signal
    ? [
        { label: 'Signal ID', value: formatValue(signal.id) },
        { label: 'User ID', value: formatValue(signal.user_id) },
        { label: 'Trigger source', value: formatValue(signal.trigger_source) },
        { label: 'Status', value: formatValue(signal.status) },
        { label: 'Attempt count', value: formatValue(signal.attempt_count) },
        { label: 'Distress level', value: formatValue(signal.distress_level) },
        { label: 'Case ID', value: formatValue(signal.case_id, 'No linked case') },
        { label: 'Last attempt', value: formatDateTime(signal.last_attempt_at) },
        { label: 'Last broadcast', value: formatDateTime(signal.last_broadcast_at) },
        { label: 'Block expires', value: formatDateTime(signal.block_expires_at, 'No active block') },
        { label: 'Signal expires', value: formatDateTime(signal.expires_at) },
        { label: 'Created', value: formatDateTime(signal.created_at) },
        { label: 'Updated', value: formatDateTime(signal.updated_at) },
      ]
    : [];

  const escalationItems = signal
    ? [
        { label: 'Broadcasted', value: flags.isEscalatedToBroadcast ? 'Yes' : 'No' },
        { label: 'Linked case', value: flags.hasLinkedCase ? 'Yes' : 'No' },
        { label: 'Active block', value: flags.hasBlock ? 'Yes' : 'No' },
        { label: 'Resolved by', value: formatValue(signal.resolved_by, 'Not resolved') },
        { label: 'Resolved at', value: formatDateTime(signal.resolved_at, 'Not resolved') },
      ]
    : [];

  const caseItems = detailState.data?.case
    ? [
        { label: 'Case ID', value: formatValue(detailState.data.case.case_id) },
        { label: 'Status', value: formatValue(detailState.data.case.status) },
        { label: 'Priority', value: formatValue(detailState.data.case.priority) },
        { label: 'Summary', value: formatValue(detailState.data.case.summary) },
        { label: 'Assigned to', value: formatValue(detailState.data.case.assigned_to, 'Unassigned') },
        { label: 'Created at', value: formatDateTime(detailState.data.case.created_at) },
        { label: 'Updated at', value: formatDateTime(detailState.data.case.updated_at) },
      ]
    : [];

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', lg: DRAWER_WIDTH },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            sx={{ px: 3, py: 2.5 }}
          >
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <Typography variant="h6">SOS Signal Detail</Typography>
              <Typography variant="body2" color="text.secondary">
                Review incident state, escalation history, linked case context, and permitted admin actions.
              </Typography>
              {signal ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <AdminStatusChip status={signal.status} />
                  <Chip label={`Distress Level ${formatValue(signal.distress_level, '0')}`} color="error" variant="outlined" />
                  <Chip label={`Attempt ${formatValue(signal.attempt_count, '0')}`} variant="outlined" />
                  <Chip label={signal.case_id ? 'Case Linked' : 'No Case'} color={signal.case_id ? 'warning' : 'default'} variant="outlined" />
                  {signal.block_expires_at ? (
                    <Chip label={`Blocked Until ${formatDateTime(signal.block_expires_at)}`} color="warning" variant="outlined" />
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
            <IconButton onClick={onClose} aria-label="Close SOS details">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
            {detailState.loading ? <DetailLoadingState /> : null}

            {!detailState.loading && detailState.error ? (
              <Alert severity="error" variant="outlined">
                {detailState.error}
              </Alert>
            ) : null}

            {!detailState.loading && !detailState.error && !signal ? (
              <AdminNoDataState
                title="No SOS signal loaded"
                description="Open the drawer from an SOS row to load signal detail, timeline events, and linked case context."
              />
            ) : null}

            {!detailState.loading && !detailState.error && signal ? (
              <Stack spacing={3}>
                <AdminActionBar
                  title="Available Actions"
                  description="Only actions documented by the SOS backend contract are available here."
                  actions={[
                    ...(flags.canResolve
                      ? [{ label: 'Resolve Signal', color: 'success', variant: 'contained', icon: 'solar:check-circle-bold' }]
                      : []),
                    ...(flags.canExtend
                      ? [{ label: 'Extend Monitoring', color: 'warning', variant: 'outlined', icon: 'solar:clock-circle-bold' }]
                      : []),
                    ...(flags.canRemoveBlock
                      ? [{ label: 'Remove Block', color: 'error', variant: 'outlined', icon: 'solar:danger-circle-bold' }]
                      : []),
                  ]}
                  onActionClick={(action) => {
                    if (action.label === 'Resolve Signal') {
                      openActionDialog('resolve');
                    } else if (action.label === 'Extend Monitoring') {
                      openActionDialog('extend');
                    } else if (action.label === 'Remove Block') {
                      openActionDialog('remove_block');
                    }
                  }}
                />

                <AdminDetailPanel title="Incident Summary" items={summaryItems} />
                <AdminDetailPanel title="Escalation Metrics" items={escalationItems} />
                <AdminTimeline title="Signal Timeline" items={signalTimelineItems} />

                {detailState.data?.case ? (
                  <>
                    <AdminDetailPanel title="Linked Case Summary" items={caseItems} />
                    <AdminTimeline title="Linked Case Timeline" items={caseTimelineItems} />
                  </>
                ) : (
                  <AdminNoDataState
                    title="No linked SOS case yet"
                    description="Cases begin from the third distress attempt, so early signals may not have a linked case payload."
                  />
                )}
              </Stack>
            ) : null}
          </Box>

          <Divider />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ px: 3, py: 2 }}>
            <Button onClick={onClose}>Close</Button>
            {flags.canResolve ? (
              <Button variant="contained" color="success" onClick={() => openActionDialog('resolve')}>
                Resolve
              </Button>
            ) : null}
            {flags.canExtend ? (
              <Button variant="outlined" color="warning" onClick={() => openActionDialog('extend')}>
                Extend
              </Button>
            ) : null}
            {flags.canRemoveBlock ? (
              <Button variant="outlined" color="error" onClick={() => openActionDialog('remove_block')}>
                Remove Block
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Drawer>

      <SosSignalActionDialog
        open={dialogState.open}
        mode={dialogState.mode}
        signal={signal}
        draft={dialogState.draft}
        error={dialogState.error}
        submitting={dialogState.submitting}
        onDraftChange={handleDraftChange}
        onClose={closeActionDialog}
        onSubmit={handleSubmitAction}
      />
    </>
  );
}

SosSignalDetailsDrawer.propTypes = {
  initialAction: PropTypes.oneOf(['resolve', 'extend', 'remove_block']),
  onClose: PropTypes.func.isRequired,
  onInitialActionHandled: PropTypes.func.isRequired,
  onSignalUpdated: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  signalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
